import "./_style.less";
import Paragraph from "antd/lib/typography/Paragraph";
import { useTranslation } from "react-i18next";
import React, { FC, useMemo } from "react";
import { RadioProps, Space } from "antd";
import moment from "moment";
import { Patient } from "../../../../models/Patient";
import clsx from "clsx";
import { useAppSelector } from "../../../../stores/hooks";
import { VuiAvatar } from "../../../../@vendor/components";
import UserEditIcon from "../../../../components/icons/UserEdit";
import { PatientModal } from "../../../../components";
import { useDisclosure } from "../../../../hooks/use-disclosure";

interface ICustomerCard {
  data: Patient;
  onSelect?: (v: Patient | null) => void;
  refetch?: () => void;
  isSelected?: boolean;
}

const CustomerCard: FC<ICustomerCard & RadioProps> = (props) => {
  const { data, onSelect, refetch, isSelected = false } = props;
  const { account } = useAppSelector((state) => state.system);
  const { t } = useTranslation();
  const { onClose, isOpen, onOpen } = useDisclosure();

  const isNotCompleteData = useMemo(() => {
    return (
      account?.id === data?.user_id &&
      (!data?.name || !data?.address || !data?.date_of_birth || !data.gender)
    );
  }, [account, data]);

  const isMySelf = useMemo(() => {
    return account?.customer_id === data?.id;
  }, [account, data]);

  const getStatus = () => {
    if (isNotCompleteData) {
      return {
        className: "status-incomplete",
        text: t("common.text.incomplete"),
      };
    }
    if (isMySelf) {
      return { className: "status-myself", text: t("common.text.myself") };
    }
    return { className: "status-complete", text: t("common.text.complete") };
  };

  const status = getStatus();

  const name = useMemo(() => data?.name || "-", [data]);

  const dob = useMemo(() => {
    if (!data?.date_of_birth) return;
    const age = moment().diff(moment(data.date_of_birth), "years");

    return (
      <Paragraph className="patient-card-date">
        {moment(data.date_of_birth).format("DD MMMM YYYY")} ({age} Years)
      </Paragraph>
    );
  }, [data]);

  const email = useMemo(() => data?.email || "-", [data]);

  return (
    <>
      <PatientModal
        data={data}
        visible={isOpen}
        onSubmit={() => {
          if (refetch) refetch();
        }}
        showNotification={false}
        onClose={onClose}
        title={t("common.text.completeYourProfile")}
      />

      <div
        className={clsx("customer-card-new", {
          selected: isSelected,
          "not-completed": isNotCompleteData,
        })}
        onClick={() => {
          if (!isNotCompleteData && onSelect) {
            onSelect(data);
          }
        }}
      >
        <div
          className={clsx("patient-card-content-wrapper", {
            "not-completed": isNotCompleteData,
          })}
        >
          <div className={`patient-card-status-wrapper ${status.className}`}>
            {status.text}
          </div>

          <div className="patient-card-info-wrapper">
            <div>
              <VuiAvatar src={data.photo?.url} />
            </div>
            <Space direction="vertical" size={6}>
              <Paragraph className="patient-card-name">{name}</Paragraph>
              <Space direction="vertical" size={2}>
                {dob}
                <Paragraph className="patient-card-email">{email}</Paragraph>
              </Space>
            </Space>
          </div>

          {isNotCompleteData && (
            <div className="patient-card-action-wrapper">
              <UserEditIcon style={{ width: 24, height: 24 }} />
              <button
                className="plain-btn complete-profile-btn"
                onClick={onOpen}
              >
                Complete Profile
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CustomerCard;
